import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { money, percent, dateTime, modelNaOfercie, quoteStatusLabel } from '../format.ts'

interface Item {
  id: string
  model: string
  manufacturerName: string
  catalogCode: string
  quantity: number
  unitRetailPrice: string
  optionsTotal: string
  discountPct: string
  unitNetPrice: string
  lineNet: string
  /** currency of the line's prices — 'EUR' for machines priced in EUR (ILMER) */
  currency: string
  /** lineNet in PLN with the offer's rate; null when NBP was unreachable */
  lineNetPln: number | null
  options: { id: string; name: string; price: string; quantity: number; optionGroup?: string | null; variantModel?: string | null; variantCode?: string | null }[]
  marginPln?: string | null
}
interface Quote {
  id: string
  number: string | null
  status: string
  title: string | null
  currency: string
  kurs: { rate: string; tableNo: string | null; rateDate: string | null; frozen: boolean } | null
  netTotal: string | null
  vatTotal: string | null
  grossTotal: string | null
  eurRate: string | null
  nbpTableNo: string | null
  nbpRateDate: string | null
  approvalRequired: boolean
  approvalReason: string | null
  approvedAt: string | null
  isGrantOffer: boolean
  validityMonths: number
  marginIsLive?: boolean
  issuedAt: string | null
  validUntil: string | null
  salesCode: string | null
  salesName: string | null
  customer: { name: string; street: string | null; postalCode: string | null; city: string | null; nip: string | null }
  items: Item[]
}

const STATUS_STYLE: Record<string, string> = {
  szkic: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-800',
  wyslana: 'bg-blue-100 text-blue-700',
  zaakceptowana: 'bg-green-100 text-green-700',
  odrzucona: 'bg-red-100 text-red-700',
  wygasla: 'bg-slate-100 text-slate-400',
}

/** A sent offer: a document, not a form. Read-only by design (W9). */
export function QuotePage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const { user } = useAuth()
  const [q, setQ] = useState<Quote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [fakturaNumer, setFakturaNumer] = useState('')
  const isOwner = user?.role === 'wlasciciel'

  function reload() {
    if (!id) return
    api
      .get<{ quote: Quote }>(`/api/quotes/${id}`)
      .then((r) => setQ(r.quote))
      .catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  async function approve() {
    if (!q) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/quotes/${q.id}/approve`)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  async function decide(decision: 'zaakceptowana' | 'odrzucona') {
    if (!q) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/api/quotes/${q.id}/decision`, { decision })
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  async function createOrder() {
    if (!q) return
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<{ order: { id: string } }>('/api/customer-orders', { quoteId: q.id })
      nav(`/zamowienia/${r.order.id}`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się utworzyć zamówienia')
      setBusy(false)
    }
  }

  if (error && !q) return <div className="p-6 text-red-700">{error}</div>
  if (!q) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const wyslana = Boolean(q.issuedAt)
  const czekaNaMnie = q.status === 'pending_approval' && isOwner
  const wygasla = Boolean(q.validUntil && q.validUntil < new Date().toISOString().slice(0, 10))
  const marzaRazem = q.items.reduce((a, i) => a + Number(i.marginPln ?? 0), 0)
  const marzaZnana = q.items.every((i) => i.marginPln != null)

  return (
    <div className="p-6 max-w-4xl">
      <Link to="/oferty" className="text-sm text-slate-500 hover:text-slate-700">
        ← Oferty
      </Link>

      <div className="mt-2 mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            {q.number ?? 'Szkic oferty'}
            {q.isGrantOffer && (
              <span className="ml-2 bg-brand-100 text-brand-700 px-2 py-0.5 rounded text-xs align-middle">
                do wniosku o dotację
              </span>
            )}
          </h1>
          <div className="text-sm text-slate-500 mt-0.5">
            {q.customer.name}
            {q.title && ` · ${q.title}`}
          </div>
          <div className="flex gap-2 mt-1.5 text-xs items-center">
            <span className={`px-2 py-0.5 rounded ${STATUS_STYLE[q.status] ?? ''}`}>{quoteStatusLabel(q.status)}</span>
            {q.issuedAt && <span className="text-slate-500">wystawiona {dateTime(q.issuedAt)}</span>}
            {q.validUntil && (
              <span className="text-slate-500">ważna do {new Date(q.validUntil).toLocaleDateString('pl-PL')}</span>
            )}
            {q.salesCode && <span className="text-slate-400">· {q.salesCode}</span>}
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          {!wyslana && (
            <Link
              to={`/oferty/${q.id}/edytuj`}
              className="text-sm border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md hover:border-brand-600"
            >
              Edytuj
            </Link>
          )}
          {wyslana && (
            <a
              href={`/api/quotes/${q.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-md hover:bg-brand-700"
            >
              Otwórz PDF
            </a>
          )}
        </div>
      </div>

      {/* The owner's decision, right where they land */}
      {czekaNaMnie && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="font-medium text-amber-900 mb-1">Ta oferta czeka na Twoją akceptację</div>
          <p className="text-sm text-amber-800 mb-3">
            {q.salesName ?? q.salesCode} dał rabat powyżej limitu producenta. Dopóki nie zaakceptujesz, oferta nie
            wyjdzie do klienta.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void approve()}
              disabled={busy}
              className="text-sm bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? 'Akceptuję…' : 'Akceptuję rabat'}
            </button>
            <span className="text-xs text-amber-700">
              Nie zgadzasz się? Powiedz handlowcowi — poprawi rabat i wyśle ponownie. Numer nie zostanie spalony.
            </span>
          </div>
        </div>
      )}

      {q.approvedAt && q.status !== 'pending_approval' && (
        <div className="mb-4 text-xs text-green-800 bg-green-50 border border-green-200 rounded px-3 py-1.5">
          Rabat powyżej limitu zaakceptowany {dateTime(q.approvedAt)}.
        </div>
      )}

      {/* The client's decision on a sent offer — the entry to stage 5 */}
      {q.status === 'wyslana' && (
        <div className="mb-4 bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-sm text-slate-600 mb-2">Co zdecydował klient?</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void decide('zaakceptowana')}
              disabled={busy}
              className="text-sm bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              Klient zaakceptował
            </button>
            <button
              onClick={() => void decide('odrzucona')}
              disabled={busy}
              className="text-sm border border-slate-300 text-slate-600 px-4 py-2 rounded-md hover:border-red-400 hover:text-red-700 disabled:opacity-50"
            >
              Klient odrzucił
            </button>
            {wygasla && (
              <span className="text-xs text-amber-700 ml-1">
                {isOwner
                  ? 'Oferta wygasła — jako właściciel możesz mimo to potwierdzić akceptację.'
                  : 'Oferta wygasła — akceptację wygasłej oferty potwierdza właściciel.'}
              </span>
            )}
          </div>
        </div>
      )}

      {q.status === 'zaakceptowana' && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between gap-4">
          <div className="text-sm text-green-900">
            Oferta zaakceptowana przez klienta. Można utworzyć zamówienie i zamówić maszyny u producentów.
          </div>
          <button
            onClick={() => void createOrder()}
            disabled={busy}
            className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium shrink-0"
          >
            {busy ? 'Tworzę…' : 'Utwórz zamówienie klienta'}
          </button>
        </div>
      )}

      {q.status === 'odrzucona' && (
        <div className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">
          Oferta odrzucona przez klienta.
        </div>
      )}

      {/* Items */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {q.items.map((it) => (
          <div key={it.id} className="p-4 border-b border-slate-100 last:border-0">
            <div className="flex justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{modelNaOfercie(it).model}</div>
                <div className="text-xs text-slate-400">
                  {it.manufacturerName} · {modelNaOfercie(it).catalogCode}
                  {it.quantity > 1 && ` · ${it.quantity} szt.`}
                </div>
                {it.options.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {it.options.map((o) => (
                      <li key={o.id} className="text-xs text-slate-600 flex gap-2">
                        <span className="text-slate-400">{o.optionGroup ? '·' : '+'}</span>
                        <span className="flex-1">
                          {o.optionGroup && <span className="text-slate-400">{o.optionGroup}: </span>}
                          {o.name}
                          {o.quantity > 1 && <span className="text-slate-400"> × {o.quantity}</span>}
                        </span>
                        {/* line value, so the list adds up to "wyposażenie" */}
                        <span className="tabular text-slate-400" title={o.quantity > 1 ? `${o.quantity} × ${money(o.price, it.currency)}` : undefined}>
                          {money(Number(o.price) * o.quantity, it.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="text-right shrink-0 text-sm">
                <div className="tabular font-semibold text-slate-800">{money(it.lineNet, it.currency)}</div>
                {it.currency !== q.currency && (
                  <div className="text-xs text-slate-500 tabular">{it.lineNetPln != null ? `= ${money(it.lineNetPln, q.currency)}` : 'brak kursu NBP'}</div>
                )}
                {Number(it.discountPct) > 0 && (
                  <div className="text-xs text-slate-400 mt-0.5">rabat {percent(it.discountPct)}</div>
                )}
                {isOwner && (
                  <div className="text-xs mt-1">
                    {it.marginPln ? (
                      <span className={Number(it.marginPln) > 0 ? 'text-green-700' : 'text-red-700'}>
                        marża {money(it.marginPln, q.currency)}
                      </span>
                    ) : (
                      <span className="text-slate-400" title="Brak ceny zakupu albo kursu">
                        marża —
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
        <div className="max-w-xs ml-auto text-sm space-y-1">
          <Row label="Netto" value={money(q.netTotal, q.currency)} />
          <Row label="VAT" value={money(q.vatTotal, q.currency)} />
          <Row label="Brutto" value={money(q.grossTotal, q.currency)} strong />
          {q.kurs && (
            <div className="text-[11px] text-slate-400">
              pozycje w EUR po kursie NBP {Number(q.kurs.rate).toLocaleString('pl-PL', { minimumFractionDigits: 4 })} zł
              {q.kurs.rateDate ? ` z ${q.kurs.rateDate}` : ''}
              {q.kurs.frozen ? '' : ' — zamrozi się przy wystawieniu'}
            </div>
          )}
          {isOwner && (
            <div className="pt-1 mt-1 border-t border-slate-100">
              <Row
                label={q.marginIsLive ? 'Marża szacunkowa' : 'Marża planowana'}
                value={marzaZnana ? money(marzaRazem, q.currency) : '—'}
                tone={marzaZnana && marzaRazem > 0 ? 'green' : undefined}
              />
              {q.marginIsLive && marzaZnana && (
                <div className="text-[11px] text-slate-400 text-right mt-0.5">
                  po dzisiejszym cenniku zakupu i kursie — zamrozi się przy wystawieniu
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Frozen rate — this is what makes the PDF reproducible a year from now.
          Only meaningful when a line is priced in EUR; a PLN offer freezes a
          rate too (for the margin), but the client never sees it. */}
      {q.eurRate && q.kurs?.frozen && (
        <div className="mt-3 text-xs text-slate-400">
          Kurs EUR zamrożony na ofercie: {Number(q.eurRate).toLocaleString('pl-PL', { minimumFractionDigits: 4 })} zł
          {q.nbpTableNo && ` (tabela ${q.nbpTableNo}`}
          {q.nbpRateDate && ` z ${new Date(q.nbpRateDate).toLocaleDateString('pl-PL')}`}
          {q.nbpTableNo && ')'}
        </div>
      )}

      {/* Invoice specification (D5) */}
      {wyslana && (
        <div className="mt-5 bg-white border border-slate-200 rounded-lg p-4">
          <h2 className="font-semibold text-slate-700 text-sm mb-1">Specyfikacja do faktury</h2>
          <p className="text-xs text-slate-400 mb-3">
            Faktura ma jedną pozycję — ta specyfikacja wyszczególnia klientowi, co dokładnie kupił. Wpisz numer faktury
            z WAPRO.
          </p>
          <div className="flex gap-2 items-center">
            <input
              value={fakturaNumer}
              onChange={(e) => setFakturaNumer(e.target.value)}
              placeholder="np. FV/2026/117"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-mono w-48 focus:border-brand-600"
            />
            <a
              href={`/api/quotes/${q.id}/specyfikacja?fakturaNumer=${encodeURIComponent(fakturaNumer)}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                if (!fakturaNumer.trim()) e.preventDefault()
              }}
              className={`text-sm px-3 py-1.5 rounded-md ${
                fakturaNumer.trim()
                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              Otwórz specyfikację
            </a>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}
    </div>
  )
}

function Row({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'green' | undefined }) {
  return (
    <div className="flex justify-between gap-6">
      <span className="text-slate-500">{label}</span>
      <span
        className={`tabular ${strong ? 'font-semibold text-slate-800 text-base' : 'text-slate-700'} ${
          tone === 'green' ? 'text-green-700 font-semibold' : ''
        }`}
      >
        {value}
      </span>
    </div>
  )
}
